import { Component, OnInit } from '@angular/core';

import { PreflightSearchService } from '@service/preflight-search/preflight-search.service';
import { ToastService } from '@service/toast/toast.service';
import { PaginationSet } from '@model/paginationset/paginationset';
import { CategoriesService } from '@service/categories.service';
import { Blog } from '@model/entities/blog';
import { BlogService } from '@service/blog.service';
import { Category } from '@model/category/category';
import { CategoryType } from '@enum/category-type';
import { MobileService } from '@service/mobile.service';
import { ScrollService } from '@service/scroll.service';
import { SentryService } from '@service/sentry.service';
import { EntityTypeConfigService } from '@service/entity-type-config.service';
import { EntityTypeIdentifier } from '@type/entity-type-identifier';
import { EntityComponent } from '@component/entity-component';
import { RestService } from '@service/rest/rest.service';
import { LoadingService } from '@service/loading.service';
import { StateService } from '@service/state.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SliderImagesService } from '@service/slider-images.service';

@Component({
  selector: 'app-blog',
  templateUrl: './blog.component.html',
  styleUrls: ['./blog.component.scss'],
})
export class BlogComponent extends EntityComponent implements OnInit {
  public pagination: PaginationSet<Blog>;

  public categories: Category[] = [];

  public preflightValues: string[];

  public mostViewed: Blog[] = [];

  public entityTypeIdentifier: EntityTypeIdentifier = 'blog';

  public hasSliderImages: boolean = false;

  constructor(
    private categoriesService: CategoriesService,
    private toastService: ToastService,
    private blogService: BlogService,
    private preflightSearchService: PreflightSearchService,
    public MobileService: MobileService,
    public ScrollService: ScrollService,
    protected RestService: RestService,
    public SentryService: SentryService,
    public EntityTypeConfigService: EntityTypeConfigService,
    public StateService: StateService,
    public LoadingService: LoadingService,
    public Router: Router,
    public ActivatedRoute: ActivatedRoute,
    public sliderImagesService: SliderImagesService,
  ) {
    super(EntityTypeConfigService, ScrollService, RestService, SentryService, StateService, Router, ActivatedRoute);
  }

  public lastCategory: string | undefined;
  public lastSearchString: string | undefined;
  public lastPagination: string | undefined;

  ngOnInit() {
    const currentCategory = this.StateService.queryParams.blog_category_id;
    const currentSearchString = this.StateService.queryParams.search_string;
    const currentPagination = this.StateService.queryParams.pagination;

    this.lastCategory = currentCategory;
    this.lastSearchString = currentSearchString;
    this.lastPagination = currentPagination;

    super.ngOnInit();

    this.getCategories();

    this.blogService
      .getMostViewed()
      .then((mostViewed) => {
        this.mostViewed = mostViewed;
      })
      .catch((err) => {
        this.SentryService.silentCaptureException(err);
      });

    this.sliderImagesService.hasSliderImages$.subscribe((hasImages: boolean) => {
      this.hasSliderImages = hasImages;
    });

    this.sliderImagesService.checkHasSliderImages('blog');
  }

  public getEntities(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.LoadingService.startLoad();

      const currentSearchString = this.StateService.queryParams.search_string;
      const currentCategory = this.StateService.queryParams.blog_category_id;
      const currentPagination = this.StateService.queryParams.pagination;

      // remove pagination from the query if the query string is different
      if (currentSearchString !== this.lastSearchString && this.lastSearchString) {
        this.StateService.queryParams = { ...this.StateService.queryParams, pagination: null };
      }

      // remove pagination from the query if the query category is different
      if (currentCategory !== this.lastCategory) {
        this.StateService.queryParams = { ...this.StateService.queryParams, pagination: null };
      }

      // capture the current state for subsequent checks
      this.lastSearchString = currentSearchString;
      this.lastCategory = currentCategory;
      this.lastPagination = currentPagination;

      this.Router.navigate([], { queryParams: this.StateService.queryParams });

      this.blogService
        .searchBlogs(this.StateService.queryParams)
        .then((blogs) => {
          this.pagination = blogs;
        })
        .catch((err) => {
          if (err && err.error && err.error.message == 'no result') {
            this.toastService.showWarning('Es konnten keine Ergebnisse zu Ihrer Suche gefunden werden.');
          } else {
            this.toastService.showError('Beiträge konnten nicht abgerufen werden.');
          }
        })
        .finally(() => {
          this.LoadingService.stopLoad();
          resolve();
        });
    });
  }

  private getCategories(): void {
    this.categoriesService
      .getCategories(CategoryType.blog)
      .then((categories) => {
        this.categories = categories;
      })
      .catch((err) => {
        this.toastService.showError('Beitragskategorien konnten nicht abgerufen werden.');
      });
  }

  public onPreflightSearch(query: string): void {
    this.preflightValues = [];

    if (query.length < 3) return;

    this.preflightSearchService.doPreflightSearch({ tags: 'BLOG', search_string: query }).then((preflightValues) => {
      this.preflightValues = preflightValues;
    });
  }

  public catFilterSet() {
    return typeof this.StateService.queryParams['blog_category_id'] != 'undefined';
  }
}
